// یادداشتهای تامین اعتبار — پنجرهٔ روی صفحه
// props: { user, hazinehDocumentId=0, budgetTransactionId=0, contractorRequestId=0, title?, onClose }
//   از سند هزینه: hazinehDocumentId ؛ از ریز سند هزینه: budgetTransactionId (اولین شناسهٔ BTId) ؛ بقیه صفر.
import React, { useState, useEffect } from 'react';
import { ui } from '../../components/ListPage';
import { ExcelButton } from '../../components/excel';
import WarningDialog from '../../components/WarningDialog';
import { useFormButtons } from '../../utils/vbPerms';
import { jsonOf, netErr } from '../../utils/http';

const toFa = n => String(n ?? '').replace(/[0-9]/g, d => '۰۱۲۳۴۵۶۷۸۹'[d]);

const API = (path, opts = {}) => fetch(`/api/tadarokat/hazineh${path}`, {
  headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('access')}` },
  ...opts,
}).then(jsonOf, netErr);

const COLS = [{ key: 'saved_user', label: 'ثبت کننده' }, { key: 'saved_time', label: 'زمان ثبت' }, { key: 'note', label: 'یادداشت' }];
const SEER_COLS = [{ key: 'seer', label: 'مشاهده کننده' }, { key: 'seen_time', label: 'زمان مشاهده' }];

const firstId = v => parseInt(String(v ?? '0').split(')(')[0], 10) || 0;

function Rows({ cols, rows, selId, onSelect }) {
  return (
    <table style={ui.table}>
      <thead><tr>{cols.map(c => <th key={c.key} style={ui.th}>{c.label}</th>)}</tr></thead>
      <tbody>
        {rows.length === 0 && <tr><td colSpan={cols.length} style={ui.empty}>موردی یافت نشد</td></tr>}
        {rows.map((r, i) => (
          <tr key={r.id ?? i} onClick={() => onSelect && onSelect(r)}
            style={{ background: selId && r.id === selId ? '#e8f0fe' : (i % 2 ? '#fdf6ec' : '#fff'), cursor: 'pointer' }}>
            {cols.map(c => (
              <td key={c.key} style={{ ...ui.td, ...(c.key === 'note' ? { whiteSpace: 'pre-wrap', textAlign: 'right' } : {}) }}>{toFa(r[c.key])}</td>
            ))}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function HavalehDetailNotes({ user, hazinehDocumentId = 0, budgetTransactionId = 0, contractorRequestId = 0, title, onClose }) {
  const fb = useFormButtons('FRMHAVALEHDETAILNOTES', ['BTNNEW', 'BTNEDIT', 'BTNDELETE', 'BTNOK', 'BTNCANCEL']);
  const keys = `budget_transaction_id=${firstId(budgetTransactionId)}&hazineh_document_id=${firstId(hazinehDocumentId)}`
    + `&contractor_request_id=${firstId(contractorRequestId)}`;
  const [rows, setRows] = useState([]);
  const [sel, setSel] = useState(null);
  const [seers, setSeers] = useState([]);
  const [mode, setMode] = useState(null);       // new | edit
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [askDel, setAskDel] = useState(false);

  const load = (keepId) => API(`/notes/?${keys}`).then(j => {
    if (!j.ok) { setError(j.msg); return; }
    setRows(j.data);
    setSel(j.data.find(r => r.id === keepId) || j.data[0] || null);
  });
  useEffect(() => { if (fb.open) load(); }, [fb.open, keys]);
  useEffect(() => {
    if (!sel) { setSeers([]); return; }
    API(`/notes/${sel.id}/seers/`).then(j => setSeers(j.ok ? j.data : []));
  }, [sel && sel.id]);

  const owner = sel && sel.mine && seers.length === 0;
  const save = async () => {
    setError('');
    const body = JSON.stringify({ note, ...Object.fromEntries(keys.split('&').map(p => p.split('='))) });
    const j = mode === 'new'
      ? await API('/notes/create/', { method: 'POST', body })
      : await API(`/notes/${sel.id}/update/`, { method: 'PUT', body });
    if (!j.ok) { setError(j.msg); return; }
    setMode(null); load(j.id);
  };
  const doDelete = async () => {
    setAskDel(false);
    const j = await API(`/notes/${sel.id}/delete/`, { method: 'DELETE' });
    if (!j.ok) { setError(j.msg); return; }
    load();
  };

  const open = fb.loaded && fb.open;
  return (
    <div style={ui.overlay}>
      <div style={{ ...ui.modal, maxWidth: '820px', height: '86vh' }}>
        <div style={ui.mHeader}>
          <span style={ui.mTitle}>{title || 'یادداشتهای تامین اعتبار'}</span>
          <button style={ui.closeBtn} onClick={onClose}>✕</button>
        </div>
        <div style={{ ...ui.mBody, display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {error && <div style={ui.errBox}>{error}</div>}
          {open && (
            <>
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {!mode && fb.can('BTNNEW') && <button style={ui.btnNew} onClick={() => { setNote(''); setMode('new'); }}>＋ جدید</button>}
                {!mode && fb.can('BTNEDIT') && owner && <button style={{ ...ui.btnEdit, cursor: 'pointer' }} onClick={() => { setNote(sel.note); setMode('edit'); }}>✎ ویرایش</button>}
                {!mode && fb.can('BTNDELETE') && owner && <button style={{ ...ui.btnDel, cursor: 'pointer' }} onClick={() => setAskDel(true)}>✕ حذف</button>}
                {mode && fb.can('BTNOK') && fb.can(mode === 'new' ? 'BTNNEW' : 'BTNEDIT') && <button style={ui.btnSave} onClick={save}>ثبت</button>}
                {mode && fb.can('BTNCANCEL') && <button style={ui.btnCancel} onClick={() => setMode(null)}>لغو</button>}
                <ExcelButton title="یادداشتهای تامین اعتبار" columns={COLS} rows={rows} />
              </div>
              <div style={{ flex: '1 1 45%', overflow: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>
                <Rows cols={COLS} rows={rows} selId={sel && sel.id} onSelect={r => !mode && setSel(r)} />
              </div>
              <div style={{ display: 'flex', gap: '12px', fontSize: '12px' }}>
                <span><span style={ui.mlabel}>نام کاربر: </span>{mode === 'new' ? '' : (sel ? sel.saved_user : '')}</span>
                <span><span style={ui.mlabel}>زمان : </span>{mode === 'new' ? '' : toFa(sel ? sel.saved_time : '')}</span>
              </div>
              <label style={ui.mlabel}>یادداشت</label>
              <textarea style={{ ...ui.input, minHeight: '80px' }} readOnly={!mode}
                value={mode ? note : (sel ? sel.note : '')} onChange={e => setNote(e.target.value)} />
              <div style={{ flex: '1 1 25%', overflow: 'auto', border: '1px solid #eee', borderRadius: '8px' }}>
                <Rows cols={SEER_COLS} rows={seers} />
              </div>
            </>
          )}
        </div>
      </div>
      {askDel && (
        <WarningDialog title="هشدار" lines={['آيا از حذف اين رکورد مطمئن هستيد؟']} confirmLabel="بلی" cancelLabel="خیر" tone="danger"
          onConfirm={doDelete} onCancel={() => setAskDel(false)} />
      )}
    </div>
  );
}
